import { Dispatch } from "react";
import { AppState } from "./configureStore";
import { createSlice, PayloadAction, AnyAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ILoadableState } from "../definitions/ILoadableState";
import { client,Path } from "../App";
import { GetCMHistory, AuthenticateUser, AuthenticateUserResponse, IsXSPSessionClosed, UpdateSessionActivity, DCRLogout, UpdateActiveInstitutionResponse, UpdateActiveInstitution } from "../DCR.dtos"
import { historyActionCreators } from "./HistoryStore";
import {accountActionCreators} from './AccountStore';
import { detailsActionCreators } from './DetailsStore';
import { schedulePaymentActionCreators } from './SchedulePaymentStore';
import { push } from "connected-react-router";
import { AppDispatch } from "..";

interface Error {
    errorMessage: string;
}

export interface IDCRState extends ILoadableState {
    currentuserInfo: AuthenticateUserResponse;
    selectedInstitution: string;
    authenticated: boolean;
}

const initialDCRState: IDCRState = {
    currentuserInfo: {} as AuthenticateUserResponse,
    isLoading: false,
    isLoaded: false,
    selectedInstitution: "",
    authenticated: false

}

export const switchActiveInstitution = createAsyncThunk<
    UpdateActiveInstitutionResponse,
    string,
    {
        dispatch: AppDispatch;
        state: AppState;
        rejectValue: Error;
    }
>(
    'userInfo/switchActiveInstitution',
    async (institutionId, thunkApi) => {
        try {
            const response = await client.post(new UpdateActiveInstitution({ institutionId }))

            return response;

        } catch (e) {
            return thunkApi.rejectWithValue({ errorMessage: e });
        }
    }

)

const slice = createSlice({
    name: "dcr",
    initialState: initialDCRState,
    reducers: {
        resetState: () => {
            return {
                ...initialDCRState
            }
        },        
        requestCurrentUser: (state: IDCRState) => {
            return {
                ...state,
                isLoading: true,
                isLoaded: false
            }
        },
        receiveCurrentUser: (state: IDCRState, action: PayloadAction<{
            currentuserInfoResponse: AuthenticateUserResponse;
        }>) => {
            const currentuserInfo = action.payload.currentuserInfoResponse;
            let actInst: string = currentuserInfo.user.activeInstitution;
            if (!actInst && currentuserInfo.user?.institutions && currentuserInfo.user?.institutions.length>0) {
                actInst = currentuserInfo.user?.institutions[0].value;
            }
            return {
                ...state,
                currentuserInfo,
                isLoading: false,
                isLoaded: true,
                selectedInstitution: actInst,
                authenticated: true
            }
        },
        updateSelectedInstitution: (state: IDCRState, action: PayloadAction<{ umcId: string }>) => {
            const activeInstitute = action.payload.umcId;
            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                selectedInstitution: activeInstitute
            }
        }           
    },
    extraReducers: builder => {
        builder
            .addCase(switchActiveInstitution.pending, (state: IDCRState) => {
                state.isLoaded = false;
                state.isLoading = true;
            })
            .addCase(switchActiveInstitution.fulfilled, (state: IDCRState, { payload }) => {
                state.isLoaded = true;
                state.isLoading = false;
                state.selectedInstitution = payload.institutionId

            })
            .addCase(switchActiveInstitution.rejected, () => {
                //Todo?
            })
    }
})

const {
    resetState,
    requestCurrentUser,
    receiveCurrentUser,
    updateSelectedInstitution
} = slice.actions;

function fetchCurrentUser() {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const currentUserState = getState().dcr;
        if (currentUserState.isLoading || currentUserState.isLoaded) {
            return;
        }

        dispatch(requestCurrentUser());
        dispatch(historyActionCreators.requestCMHistory());

        try {

            const currentuserInfoResponse = await client.post(new AuthenticateUser());

            dispatch(receiveCurrentUser({ currentuserInfoResponse }));

            const cmHistoryInfoResponse = await client.post(new GetCMHistory({ umcId: currentuserInfoResponse.user.activeInstitution }));

            //Check for errors
            if (cmHistoryInfoResponse.responseStatus)
                throw cmHistoryInfoResponse.responseStatus;

            dispatch(historyActionCreators.receiveCMHistory({ cmHistoryInfoResponse }));
            dispatch(push(Path.ContributionHistory));

        }
        catch (e) {
            console.log(e);
            if (e.responseStatus?.errorCode?.toUpperCase() === "UNAUTHORIZED" || e.responseStatus?.errorCode?.toUpperCase() === "401") {
                dispatch(resetState());
                sessionStorage.clear();
                dispatch(push(Path.Logout));
            }
            else {
                dispatch(historyActionCreators.receiveCMHistoryError({ message: e.responseStatus?.message }))
            }
        }
    }
}



function updateActiveInstitution(umcId: string) {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestCurrentUser());
        dispatch(updateSelectedInstitution({ umcId }));       
    }
}

function logout(){
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(push(Path.Logout));
        //await client.post(new Authenticate({ provider: "logout" })); //Remove session cookie that was created 
        dispatch(resetState());
        dispatch(historyActionCreators.resetState());
        dispatch(detailsActionCreators.resetState());
        dispatch(accountActionCreators.resetState());
        dispatch(schedulePaymentActionCreators.resetState());

        try {
            await client.post(new DCRLogout());
        }
        catch (e) {
            console.log(e);
        }

        sessionStorage.clear();

    }
}

function checkXSPSession() {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const currentUserState = getState().dcr;
        if (!currentUserState.authenticated) {
            return;
        }

        try {

           const response = await client.post(new IsXSPSessionClosed());

            //Check for errors
            if (response.responseStatus){
                throw response.responseStatus;
            }

            if(response.sessionID === 0){
                dispatch(push(Path.Logout));
                dispatch(resetState());
                dispatch(historyActionCreators.resetState());
                dispatch(detailsActionCreators.resetState());
                dispatch(accountActionCreators.resetState());
                dispatch(schedulePaymentActionCreators.resetState());

                sessionStorage.clear();

                try{
                    await client.post(new DCRLogout());
                }
                catch(e) {
                    console.log(e);
                }                
            }
        }
        catch (e) {
            console.log(e);
        }
    }
}

function updateXSPSession() {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const currentUserState = getState().dcr;
        if (!currentUserState.authenticated) {
            return;
        }

        try {
           const response = await client.post(new UpdateSessionActivity());

            //Check for errors
            if (response.responseStatus){
                throw response.responseStatus;
            }
        }
        catch (e) {
            console.log(e);
        }
    }
}

export const dcrReducer = slice.reducer;

export const dcrActionCreators = {
    fetchCurrentUser,
    updateActiveInstitution,
    logout,
    checkXSPSession,
    updateXSPSession,
    switchActiveInstitution
};